import React from 'react';
import styled from 'styled-components';
import {
  AviveStatusIcon,
  XCloseIcon,
  ERAShowIcon,
  PlusIcon,
} from '@rsos/assets/icons';
import AviveCheckIcon from '@rsos/assets/icons/src/AviveCheckIcon';
import AviveWarningIcon from '@rsos/assets/icons/src/AviveWarningIcon';
import { Box, Text } from '@rsos/components/capstone/base';
import { Button, StyledIcon } from './AviveStatus.styles';
import {
  AVIVE2_STATUS_AED_ACCEPTED_APP_ACCEPTED,
  AVIVE2_STATUS_AED_NEARBY_APP_ACCEPTED,
  AVIVE2_STATUS_DEVICES_NEARBY_NOT_APP_ACCEPTED,
  AVIVE2_STATUS_DEVICE_ACCEPTED_NOT_APP_ACCEPTED,
  AVIVE2_STATUS_NO_AED_FOUND_APP_ACCEPTED,
  AVIVE2_STATUS_NO_AED_FOUND_NOT_APP_ACCEPTED,
} from './aviveStatusTypes';

export const IconTextBox = styled(Text)`
  display: grid;
  grid-template-columns: 20px auto;
  grid-gap: 5px;
  align-items: flex-start;
  margin-left: 10px;
`;

export const InnerIconTextBox = styled(IconTextBox)`
  margin-left: 0;
`;

const StyledAviveEventStatus = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  border-radius: 5px;
  padding: 20px;
`;

const StyledAviveInnerStatus = styled(StyledAviveEventStatus)`
  margin-top: 10px;
  padding: 10px;
  width: 100%;
`;

const findStatusComponent = status => {
  switch (status) {
    case AVIVE2_STATUS_AED_ACCEPTED_APP_ACCEPTED:
      return (
        <>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="success">
              <AviveCheckIcon />
              <Text>AED device(s) accepted, are en route.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="success">
              <AviveCheckIcon />
              <Text>App responders accepted, are en route. </Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
        </>
      );
    case AVIVE2_STATUS_DEVICE_ACCEPTED_NOT_APP_ACCEPTED:
      return (
        <>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="success">
              <AviveCheckIcon />
              <Text>AED device(s) accepted, are en route.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="secondaryText">
              <XCloseIcon />
              <Text>No app responders accepted. </Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
        </>
      );
    case AVIVE2_STATUS_AED_NEARBY_APP_ACCEPTED:
      return (
        <>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="primaryBase">
              <ERAShowIcon height={15} width={15} />
              <Text>AED device(s) nearby.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="success">
              <AviveCheckIcon />
              <Text>App responders accepted, are en route.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
        </>
      );
    case AVIVE2_STATUS_DEVICES_NEARBY_NOT_APP_ACCEPTED:
      return (
        <>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="primaryBase">
              <ERAShowIcon height={15} width={15} />
              <Text>AED device(s) nearby.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="secondaryText">
              <XCloseIcon />
              <Text>No app responders accepted.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
        </>
      );
    case AVIVE2_STATUS_NO_AED_FOUND_APP_ACCEPTED:
      return (
        <>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="lightDanger">
              <XCloseIcon />
              <Text>No AED device(s) found.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="success">
              <AviveCheckIcon />
              <Text>App responders accepted, are en route. </Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
        </>
      );
    case AVIVE2_STATUS_NO_AED_FOUND_NOT_APP_ACCEPTED:
      return (
        <>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="lightDanger">
              <XCloseIcon />
              <Text>No AED device(s) found.</Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
          <StyledAviveInnerStatus>
            <InnerIconTextBox size="small" color="secondaryText">
              <XCloseIcon />
              <Text>No app responders accepted. </Text>
            </InnerIconTextBox>
          </StyledAviveInnerStatus>
        </>
      );

    default:
      return <></>;
  }
};

const AviveEventStatus = ({
  status,
  onNewNote = () => {},
  onCancel = () => {},
  isNotesSending = false,
}) => {
  return (
    <StyledAviveEventStatus status={status}>
      <Box>
        <IconTextBox inline size="normal" fontWeight="bold">
          <StyledIcon brand>
            <AviveStatusIcon />
          </StyledIcon>
          <Text color="primaryText">Avive AED request made.</Text>
        </IconTextBox>

        <IconTextBox size="small" color="secondaryText">
          <AviveWarningIcon />
          <Text>
            <strong>Zoom out</strong> to see devices potentially out of view.
          </Text>
        </IconTextBox>
        {findStatusComponent(status)}
      </Box>
      <Box horizontal mt={2} align="center" justify="center">
        <Button btnType="secondary" onClick={onCancel}>
          Cancel Request
        </Button>
        <Button onClick={onNewNote} disabled={isNotesSending}>
          <PlusIcon width={10} height={10} />
          <span style={{ marginLeft: 5 }}>New AED Note</span>
        </Button>
      </Box>
    </StyledAviveEventStatus>
  );
};

export default AviveEventStatus;
